import { produce } from '@utils';
import type { CadetParser } from './CadetParser';
import * as TT from './CadetTokens';
import { nestableLevel, type Container, type Nestable } from './Container';
import type { Unparser } from './Unparser';
import { canonicalizeLocale, isWellFormedLocale } from './i18nLocale';

export abstract class Value implements Nestable {
  start?: number;
  parent?: Container;
  rawValue: unknown;

  constructor(rawValue: unknown, opts?: { start?: number; parent?: Container }) {
    this.rawValue = rawValue;
    this.start = opts?.start;
    this.parent = opts?.parent;
  }

  // eslint-disable-next-line @typescript-eslint/class-literal-property-style
  get isComplex(): boolean {
    return false;
  }

  get level(): number {
    return nestableLevel(this);
  }

  static parseValue(p: CadetParser, parent?: Container): Value | undefined {
    let value: Value | undefined;
    // Try IStringValue before StringValue so we can capture sequences like
    // "text"~en-US "autre"~fr-FR
    value = IStringValue.parse(p, parent);
    if (value) {
      return value;
    }
    value = BooleanValue.parse(p, parent);
    if (value) {
      return value;
    }
    value = NumberValue.parse(p, parent);
    if (value) {
      return value;
    }
    value = StringValue.parse(p, parent);
    if (value) {
      return value;
    }
    value = NullValue.parse(p, parent);
    if (value) {
      return value;
    }
    value = ArrayValue.parse(p, parent);
    if (value) {
      return value;
    }
    value = ObjectValue.parse(p, parent);
    if (value) {
      return value;
    }
    return undefined;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  toJSON(opts?: { compact?: boolean }): object {
    return {
      start: this.start,
      parent: this.parent?.constructor.name,
      rawValue: this.rawValue
    };
  }

  abstract unparse(u: Unparser): void;
}

export class BooleanValue extends Value {
  get value(): boolean {
    return this.rawValue as boolean;
  }

  static parse(p: CadetParser, parent?: Container): BooleanValue | undefined {
    let token = p.consumeToken(TT.TRUE);
    if (token) {
      return new BooleanValue(true, { start: token.start, parent });
    }
    token = p.consumeToken(TT.FALSE);
    if (token) {
      return new BooleanValue(false, { start: token.start, parent });
    }
    return undefined;
  }

  unparse(u: Unparser): void {
    u.add(this.value ? 'true' : 'false');
  }
}

export class NumberValue extends Value {
  get value(): number {
    return this.rawValue as number;
  }

  static parse(p: CadetParser, parent?: Container): NumberValue | undefined {
    const token = p.consumeToken(TT.NUMBER);
    if (token) {
      return new NumberValue(token.value, { start: token.start, parent });
    }
    return undefined;
  }

  unparse(u: Unparser): void {
    u.add(this.value.toString());
  }
}

export class StringValue extends Value {
  get value(): string {
    return this.rawValue as string;
  }

  static parse(p: CadetParser, parent?: Container): StringValue | undefined {
    const token = p.string();
    if (token) {
      return new StringValue(token.value, { start: token.start, parent });
    }
    return undefined;
  }

  unparse(u: Unparser): void {
    u.add(JSON.stringify(this.value));
  }
}

// Internationalized string value: one or more segments of
//    <JSONString> ~ <locale>
export class IStringValue extends Value {
  // rawValue will be a record: { variants: Record<string, string> }
  // where keys are canonical BCP 47 locale tags
  get variants(): Record<string, string> {
    return (this.rawValue as { variants: Record<string, string> }).variants;
  }

  static parse(p: CadetParser, parent?: Container): IStringValue | undefined {
    return p.withSavedPosition(commit => {
      // First, require a JSON string literal
      const firstStr = p.string();
      if (!firstStr) return undefined;

      // Optional breaks and attempt to parse at least one ~ LOCALE
      p.consumeBreaks();
      const tilde = p.consumeToken(TT.TILDE);
      if (!tilde) {
        // No tilde after first string: treat as plain string
        return undefined;
      }
      const localeToken = p.consumeToken(TT.LOCALE);
      if (!localeToken) {
        throw p.error('Expected locale after ~', tilde.start);
      }
      const localeRaw = localeToken.text;
      if (!isWellFormedLocale(localeRaw)) {
        throw p.error(`Invalid locale tag: ${localeRaw}`, tilde.start);
      }
      const locale = canonicalizeLocale(localeRaw);

      const variants: Record<string, string> = {};
      variants[locale] = firstStr.value;

      // Parse zero or more additional segments
      // Each additional must be: STRING [BREAKS?] ~ LOCALE
      for (;;) {
        const before = p.current;
        const nextStr = p.string();
        if (!nextStr) {
          // No more segments
          break;
        }
        const nextTilde = p.consumeToken(TT.TILDE);
        if (!nextTilde) {
          // If we encountered another string without a following ~locale,
          // this is a grammar error in an IString sequence.
          throw p.error('Expected ~locale after IString segment', before.start);
        }
        const rawToken = p.consumeToken(TT.LOCALE);
        if (!rawToken) {
          throw p.error('Expected locale after ~', nextTilde.start);
        }
        const raw = rawToken.text;
        if (!isWellFormedLocale(raw)) {
          throw p.error(`Invalid locale tag: ${raw}`, nextTilde.start);
        }
        const can = canonicalizeLocale(raw);
        if (Object.prototype.hasOwnProperty.call(variants, can)) {
          throw p.error(`Duplicate locale variant: ${can}`, nextTilde.start);
        }
        variants[can] = nextStr.value;
      }

      commit();
      return new IStringValue({ variants }, { start: firstStr.start, parent });
    });
  }

  unparse(u: Unparser): void {
    const defaultLocale = 'en-US';
    const entries = Object.entries(this.variants);
    // Deterministic order: default first if present, then alpha by locale
    const others = entries
      .filter(([loc]) => loc !== defaultLocale)
      .sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0));
    const ordered: [string, string][] = [];
    const def = entries.find(([loc]) => loc === defaultLocale);
    if (def) ordered.push(def);
    ordered.push(...others);
    if (!def && others.length === 0) {
      // No variants? Fallback to empty string (shouldn't happen by grammar)
      u.add(JSON.stringify(''));
      return;
    }
    if (ordered.length === 1 && def) {
      // Single default → plain string
      u.add(JSON.stringify(def[1]));
      return;
    }
    // Emit segments: "text"~locale separated by space
    const parts: string[] = ordered.map(
      ([loc, text]) => `${JSON.stringify(text)}~${loc}`
    );
    u.add(parts.join(' '));
  }
}

export class NullValue extends Value {
  unparse(u: Unparser): void {
    u.add('null');
  }

  static parse(p: CadetParser, parent?: Container): NullValue | undefined {
    const token = p.consumeToken(TT.NULL);
    if (token) {
      return new NullValue(null, { start: token.start, parent });
    }
    return undefined;
  }
}

export abstract class CompoundValue extends Value implements Container {
  nestingLevel: number;

  constructor(value: unknown, opts?: { start?: number; parent?: Container }) {
    super(value, opts);
    this.nestingLevel = this.parent ? this.parent.nestingLevel + 1 : 0;
  }
}

export class ArrayValue extends CompoundValue {
  values: Value[] = [];

  constructor(opts?: { start?: number; parent?: Container }) {
    super([], opts);
  }

  get isComplex(): boolean {
    return this.values.some(value => value.isComplex);
  }

  pushValue(value: Value): void {
    this.values.push(value);
    this.value.push(value.rawValue);
  }

  get value(): unknown[] {
    return this.rawValue as unknown[];
  }

  static parse(p: CadetParser, parent?: Container): ArrayValue | undefined {
    return p.withSavedPosition(commit => {
      // Consume any breaks before opening bracket
      p.consumeBreaks();

      // Check for opening bracket
      const lBracket = p.lBracket();
      if (lBracket === undefined) {
        return undefined;
      }

      // Create array value
      const arrayValue = new ArrayValue({ start: lBracket.start, parent });

      // Consume any breaks after opening bracket
      p.consumeBreaks();

      // Parse values until we find closing bracket
      while (!p.isEOT) {
        // Check for closing bracket
        if (p.rBracket()) {
          break;
        }

        // Parse value
        const value = Value.parseValue(p, arrayValue);
        if (!value) {
          throw p.error('Invalid value format', p.current.start);
        }
        arrayValue.pushValue(value);

        // A comma or break is required after each value,
        // or the closing bracket must be detected.
        const foundBreakAfterValue = p.consumeBreaks();
        const foundCommaAfterValue = p.comma() !== undefined;
        const foundBreakAfterComma = p.consumeBreaks();
        if (
          !foundBreakAfterValue &&
          !foundCommaAfterValue &&
          !foundBreakAfterComma &&
          p.current.type !== TT.RBRACKET
        ) {
          throw p.error('Invalid array format', p.current.start);
        }
      }

      commit();
      return arrayValue;
    });
  }

  unparse(u: Unparser): void {
    if (this.isComplex) {
      u.start('[');
      for (const [index, elem] of this.values.entries()) {
        elem.unparse(u);
        if (index < this.values.length - 1) {
          u.addLeft(',', '');
        }
        u.endLine();
      }
      u.end(']');
    } else {
      u.startCompact('[');
      for (const [index, elem] of this.values.entries()) {
        elem.unparse(u);
        if (index < this.values.length - 1) {
          u.addLeft(',');
        }
      }
      u.end(']');
    }
  }
}

export class KeyValuePair implements Nestable {
  key: string;
  value: Value;
  parent: ObjectValue;

  constructor(key: string, value: Value, parent: ObjectValue) {
    this.key = key;
    this.value = value;
    this.parent = parent;
  }

  get isComplex(): boolean {
    return this.value.isComplex;
  }

  get level(): number {
    return nestableLevel(this);
  }

  unparse(u: Unparser): void {
    u.add(this.key);
    u.addLeft(':');
    this.value.unparse(u);
  }
}

export class ObjectValue extends CompoundValue {
  values: KeyValuePair[] = [];

  constructor(
    initialValue?: Record<string, unknown> | { start?: number; parent?: Container }
  ) {
    // If initialValue is a record (not options), create an object from it
    if (
      initialValue !== undefined &&
      !('start' in initialValue) &&
      !('parent' in initialValue)
    ) {
      super(initialValue);

      // Create KeyValuePair objects for each property
      for (const [key, val] of Object.entries(initialValue)) {
        // Create appropriate Value subclass based on the type of the property
        let valueObj: Value;

        if (val === null) {
          valueObj = new NullValue(null);
        } else if (typeof val === 'boolean') {
          valueObj = new BooleanValue(val);
        } else if (typeof val === 'number') {
          valueObj = new NumberValue(val);
        } else if (typeof val === 'string') {
          valueObj = new StringValue(val);
        } else if (Array.isArray(val)) {
          const arrayValue = new ArrayValue();
          for (const item of val) {
            // Recursively create Value objects for array items
            let itemValue: Value;
            if (item === null) {
              itemValue = new NullValue(null);
            } else if (typeof item === 'boolean') {
              itemValue = new BooleanValue(item);
            } else if (typeof item === 'number') {
              itemValue = new NumberValue(item);
            } else if (typeof item === 'string') {
              itemValue = new StringValue(item);
            } else if (typeof item === 'object') {
              itemValue = new ObjectValue(item as Record<string, unknown>);
            } else {
              // Default to string for other types
              itemValue = new StringValue(String(item));
            }
            arrayValue.pushValue(itemValue);
          }
          valueObj = arrayValue;
        } else if (typeof val === 'object') {
          valueObj = new ObjectValue(val as Record<string, unknown>);
        } else {
          // For other types, use a fallback string representation
          // We never expect to get here since we already handled all object types
          valueObj = new StringValue('[Complex Value]');
        }

        // Create and add the key-value pair
        this.pushKeyValuePair(new KeyValuePair(key, valueObj, this));
      }
    } else {
      // Handle the case where initialValue is the options object or undefined
      super({}, initialValue as { start?: number; parent?: Container } | undefined);
    }
  }

  pushKeyValuePair(pair: KeyValuePair): void {
    this.values.push(pair);
    // this.value[pair.key] = pair.value.rawValue;
    this.rawValue = produce<Record<string, unknown>>(this.value, draft => {
      draft[pair.key] = pair.value.rawValue;
    });
  }

  // eslint-disable-next-line @typescript-eslint/class-literal-property-style
  get isComplex(): boolean {
    return true;
  }

  get value(): Record<string, unknown> {
    return this.rawValue as Record<string, unknown>;
  }

  static parse(p: CadetParser, parent?: Container): ObjectValue | undefined {
    return p.withSavedPosition(commit => {
      // Consume any breaks before opening brace
      p.consumeBreaks();

      // Check for opening brace
      const lBrace = p.lBrace();
      if (lBrace === undefined) {
        return undefined;
      }

      // Create object value
      const objectValue = new ObjectValue({ start: lBrace.start, parent });

      // Consume any breaks after opening brace
      p.consumeBreaks();

      // Parse key-value pairs until we find closing brace
      while (!p.isEOT) {
        // Check for closing brace
        if (p.rBrace()) {
          break;
        }

        // Parse key
        const keyToken = p.identifier();
        if (!keyToken) {
          throw p.error('Invalid key format', p.current.start);
        }

        // Consume breaks before colon
        p.consumeBreaks();

        // Consume colon
        if (!p.colon()) {
          throw p.error('Missing colon after key', p.current.start); // Improved error handling
        }

        // Consume any breaks after colon
        p.consumeBreaks();

        // Parse value
        const value = Value.parseValue(p, objectValue);
        if (!value) {
          throw p.error('Invalid value format', p.current.start);
        }

        // Reject duplicate key
        if (Object.prototype.hasOwnProperty.call(objectValue.value, keyToken.value)) {
          throw p.error(`Duplicate key '${keyToken.value}'`, keyToken.start);
        }

        // Add key-value pair
        objectValue.pushKeyValuePair(
          new KeyValuePair(keyToken.value, value, objectValue)
        );

        // After a value, allow optional breaks, then either:
        // - a comma (then optional breaks) and continue
        // - a closing brace
        // - or the start of the next key (an identifier) without comma
        p.consumeBreaks();
        if (p.comma()) {
          p.consumeBreaks();
          // continue to next pair
        } else if (p.current.type === TT.RBRACE) {
          // handled on next loop iteration
        } else if (p.current.type === TT.ID) {
          // allow break-separated key-value pairs without a comma
          // continue parsing next pair
        } else {
          throw p.error('Invalid object format', p.current.start);
        }
      }

      commit();
      return objectValue;
    });
  }

  unparse(u: Unparser): void {
    u.start('{');
    for (const [index, pair] of this.values.entries()) {
      pair.unparse(u);
      if (index < this.values.length - 1) {
        u.addLeft(',', '');
      }
      u.endLine();
    }
    u.end('}');
  }
}
